import { AggregateCartesianChartDataConfig } from '@app/data/interface/widget-config';
import { EChartsOption, SeriesOption } from 'echarts';
import { dataViewFormatter } from '../../../../../widgets/helpers/widgets';
import { color } from '@app/client/clients/wastemanagement/taiao-dashboard-layout/colors';
import { WidgetNumberUtils } from '@app/client/clients/wastemanagement/taiao-dashboard-layout/widget-helpers/widget-number-utils';
import {
  buildAllowedIdentifierSet,
  buildSourceIdToIdentifierMap,
  resolveNormalizedSourceIdentifier,
} from './source-filter-utils';

type VegetationCountKey = 'shrub_count' | 'sapling_count' | 'tree_count';
type VegetationSeriesConfig = { key: VegetationCountKey; name: string; color: string };

type VegetationMetadataKey = VegetationCountKey | TerrestrialVegetationCountKey;

/**
 * The total vegetation counts structure.
 */
interface VegetationTotals {
  shrub_count: number;
  sapling_count: number;
  tree_count: number;
}

/**
 * Additional metadata attached to a series when building a data view.
 */
interface VegetationSeriesDataViewMeta {
  dataViewTotals?: Record<string, VegetationTotals>;
  dataViewSpeciesOrder?: string[];
}

/**
 * Maps vegetation data to a format suitable for the chart.
 */
type TerrestrialVegetationCountKey = 'small_seedling_count' | 'large_seedling_count' | 'sapling_count' | 'tree_count';

type TerrestrialVegetationCounts = Record<TerrestrialVegetationCountKey, number>;

interface TerrestrialVegetationSeriesConfig {
  key: TerrestrialVegetationCountKey;
  name: string;
}

/**
 * The order here defines the stacking order in the chart (bottom to top)
 * @returns The order of vegetation series.
 */
const getVegetationSeriesOrder = (): ReadonlyArray<VegetationSeriesConfig> => [
  { key: 'shrub_count', name: 'Shrub', color: color[0] },
  { key: 'sapling_count', name: 'Sapling', color: color[1] },
  { key: 'tree_count', name: 'Tree', color: color[2] },
];

/**
 * Gets the order of terrestrial vegetation series.
 * @returns The order of terrestrial vegetation series.
 */
const getTerrestrialVegetationSeriesOrder = (): ReadonlyArray<TerrestrialVegetationSeriesConfig> => [
  { key: 'small_seedling_count', name: 'Small seedlings' },
  { key: 'large_seedling_count', name: 'Large seedlings' },
  { key: 'sapling_count', name: 'Saplings' },
  { key: 'tree_count', name: 'Trees' },
];
/**
 * Represents accumulated vegetation counts for a single data point.
 */
export interface VegetationAccumulatedCounts {
  shrub_count: number;
  sapling_count: number;
  tree_count: number;
}

/**
 * Parses the raw metadata for vegetation data.
 * @param metadataRaw The raw metadata to parse.
 * @returns The parsed metadata as a record, or null if parsing failed.
 */
const parseVegetationMetadata = (metadataRaw: unknown): Record<string, any> | null => {
  if (!metadataRaw) return null;
  if (typeof metadataRaw === 'string') {
    try {
      return JSON.parse(metadataRaw);
    } catch (_error) {
      return null;
    }
  }
  if (typeof metadataRaw === 'object') return metadataRaw as Record<string, any>;
  return null;
};

/**
 * Builds a lookup table for source identifiers from the series data.
 *
 * @param seriesData The series data to process.
 * @returns A record mapping source IDs to their human-readable names.
 */
export const buildSourceIdentifierLookup = (seriesData: any): Record<string, string> => {
  const lookup: Record<string, string> = {};
  const sources = Array.isArray(seriesData?.environmentalDataSources) ? seriesData.environmentalDataSources : [];

  sources.forEach((source: any) => {
    if (!source) return;
    lookup[String(source.id)] = source.source_metadata?.Treatment || 'Unknown';
  });

  return lookup;
};

/**
 * Gets the source identifier for a data point using a source lookup and an ID-to-label map.
 */
export const getSourceIdentifierFromDataPoint = (
  dataPoint: any,
  sourceLookup: Record<string, string>,
  idToLabelMap: Record<string, string>
) => {
  if (!dataPoint || typeof dataPoint !== 'object') return null;
  const key = String(dataPoint.environmental_data_source_id);
  return sourceLookup[key] ?? idToLabelMap[key] ?? key;
};

/**
 * Gets the vegetation count for a specific VegetationCountKey key from metadata.
 * @param metadata The metadata to search.
 * @param key The vegetation count key to retrieve, as defined in VegetationCountKey.
 * @returns The vegetation count for the specified VegetationCountKey key, or 0 if not found.
 */
const getVegetationCount = (metadata: Record<string, any> | null, key: VegetationMetadataKey): number => {
  if (!metadata) return 0;
  const camelKey = key.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
  const candidates = [metadata[key], metadata[camelKey], metadata[key.toUpperCase()], metadata[key.replace(/_/g, '')]];
  for (const candidate of candidates) {
    const numeric = WidgetNumberUtils.toFiniteNumber(candidate);
    if (numeric !== null) return numeric;
  }
  return 0;
};

/**
 * Initializes the vegetation counts to zero.
 * @returns The initial vegetation counts.
 */
const initialiseCounts = (): VegetationTotals => ({ shrub_count: 0, sapling_count: 0, tree_count: 0 });

/**
 * Initializes the terrestrial vegetation counts to zero.
 * @returns The initial terrestrial vegetation counts.
 */
const initialiseTerrestrialCounts = (): TerrestrialVegetationCounts => ({
  small_seedling_count: 0,
  large_seedling_count: 0,
  sapling_count: 0,
  tree_count: 0,
});

/**
 * Builds a lookup table for entity labels from the series data.
 * @param seriesData The series data to process.
 * @returns A record mapping entity IDs to their labels.
 */
const registerEntity = (lookup: Record<string, string>, entity: any) => {
  if (!entity) return;
  const id = entity.id ?? entity.entity_id ?? entity.observation_entity_id;
  if (id == null) return;
  const labelCandidate =
    entity.name || entity.display_name || entity.displayName || entity.unique_id || entity.uniqueId || entity.label;
  if (!labelCandidate) return;
  lookup[String(id)] = String(labelCandidate);
};

/**
 * Consumes entities and adds them to the lookup.
 * @param lookup The lookup table to populate.
 * @param entities The entities to consume.
 * @returns The updated lookup table.
 */
const consumeEntities = (lookup: Record<string, string>, entities: any) => {
  if (!entities) return;
  if (Array.isArray(entities)) {
    entities.forEach((entity: any) => registerEntity(lookup, entity));
    return;
  }
  if (typeof entities === 'object') {
    Object.values(entities).forEach((entity) => registerEntity(lookup, entity));
  }
};

/**
 * Builds a lookup table for entity labels from the series data.
 * @param seriesData The series data to process.
 * @returns A record mapping entity IDs to their labels.
 */
const buildEntityLabelLookup = (seriesData: any): Record<string, string> => {
  const lookup: Record<string, string> = {};

  consumeEntities(lookup, seriesData?.entities);
  consumeEntities(lookup, seriesData?.observationEntities);

  if (Array.isArray(seriesData?.environmentalDataSources)) {
    seriesData.environmentalDataSources.forEach((source: any) => {
      consumeEntities(lookup, source?.entities);
      consumeEntities(lookup, source?.observationEntities);
    });
  }

  return lookup;
};

/**
 * Collects the IDs of native entities from the series data.
 * @param seriesData The series data to process.
 * @returns A set of native entity IDs.
 */
const addEntityToNativeSet = (nativeIds: Set<string>, entity: any): void => {
  if (!entity) return;
  const id = entity.id ?? entity.entity_id ?? entity.observation_entity_id;
  if (id == null) return;
  const tags = Array.isArray(entity.tags) ? entity.tags : [];
  if (tags.some((tag: unknown) => typeof tag === 'string' && tag.toLowerCase() === 'native')) {
    nativeIds.add(String(id));
  }
};

/**
 * Consume entities (array or object) and add native ones into nativeIds set.
 */
const consumeEntitiesForNative = (nativeIds: Set<string>, entities: any): void => {
  if (!entities) return;
  if (Array.isArray(entities)) {
    entities.forEach((entity) => addEntityToNativeSet(nativeIds, entity));
    return;
  }
  if (typeof entities === 'object') {
    Object.values(entities).forEach((entity) => addEntityToNativeSet(nativeIds, entity));
  }
};

/**
 * Collects the IDs of native entities from the series data.
 * @param seriesData The series data to process.
 * @returns A set of native entity IDs.
 */
const collectNativeEntityIds = (seriesData: any): Set<string> => {
  const nativeIds = new Set<string>();

  consumeEntitiesForNative(nativeIds, seriesData?.entities);
  consumeEntitiesForNative(nativeIds, seriesData?.observationEntities);

  if (Array.isArray(seriesData?.environmentalDataSources)) {
    seriesData.environmentalDataSources.forEach((source: any) => {
      consumeEntitiesForNative(nativeIds, source?.entities);
      consumeEntitiesForNative(nativeIds, source?.observationEntities);
    });
  }

  return nativeIds;
};

/**
 * Process a single raw record for terrestrial vegetation and accumulate counts and species sets per site.
 * Extracted to module scope to reduce cyclomatic complexity inside the main loop.
 */
const processTerrestrialRecord = (
  record: any,
  sourceLookup: Record<string, string>,
  idToLabelMap: Record<string, string>,
  perSite: Map<string, TerrestrialVegetationCounts>,
  siteOrder: string[],
  nativeEntityIds: Set<string>,
  totalSpeciesBySite: Map<string, Set<string>>,
  nativeSpeciesBySite: Map<string, Set<string>>,
  terrestrialSeriesOrder: ReadonlyArray<TerrestrialVegetationSeriesConfig>
): void => {
  if (!record || typeof record !== 'object') return;

  const siteId = getSourceIdentifierFromDataPoint(record, sourceLookup, idToLabelMap);
  if (!siteId) return;

  let bucket = perSite.get(siteId);
  if (!bucket) {
    bucket = initialiseTerrestrialCounts();
    perSite.set(siteId, bucket);
    siteOrder.push(siteId);
  }
  if (record?.observation_entity_id != null) {
    const speciesKey = String(record.observation_entity_id);
    ensureSet(totalSpeciesBySite, siteId).add(speciesKey);
    if (nativeEntityIds.has(speciesKey)) {
      ensureSet(nativeSpeciesBySite, siteId).add(speciesKey);
    }
  }

  const metadata = parseVegetationMetadata(record?.collection_metadata);
  if (!metadata) return;

  for (const { key } of terrestrialSeriesOrder) {
    const value = getVegetationCount(metadata, key as VegetationMetadataKey);
    if (value !== 0) {
      bucket[key] += value;
    }
  }
};

/**
 * Ensures that a Set exists for the given key in the map.
 * @param map The map to check.
 * @param key The key to check for.
 * @returns The existing or newly created Set.
 */
const ensureSet = <T>(map: Map<string, Set<T>>, key: string): Set<T> => {
  let existing = map.get(key);
  if (!existing) {
    existing = new Set<T>();
    map.set(key, existing);
  }
  return existing;
};

/**
 * Resolve a species label for a data point using metadata and an entity lookup.
 * Prefers metadata.name when present, falls back to entity lookup by observation_entity_id,
 * and finally returns a generic placeholder.
 */
const findSpeciesLabel = (
  dataPoint: any,
  metadata: Record<string, any> | null,
  entityLookup: Record<string, string>
): string => {
  if (metadata && typeof metadata.name === 'string' && metadata.name.trim().length > 0) {
    return metadata.name.trim();
  }

  const obsEntityId = dataPoint?.observation_entity_id;
  if (obsEntityId != null) {
    const lookup = entityLookup[String(obsEntityId)];
    if (lookup) return lookup;
  }

  return 'Unknown species';
};

/**
 * Process a single series data point: parse metadata, resolve species label,
 * ensure a bucket exists and accumulate vegetation counts into that bucket.
 */
const processSeriesPoint = (
  dataPoint: any,
  entityLookup: Record<string, string>,
  ensureSpeciesBucket: (speciesLabel: string) => VegetationTotals,
  vegetationSeriesOrder: ReadonlyArray<VegetationSeriesConfig>
): void => {
  if (!dataPoint) return;
  const metadataSource = dataPoint.collection_metadata;
  const metadata = parseVegetationMetadata(metadataSource);

  const speciesLabel = findSpeciesLabel(dataPoint, metadata, entityLookup);
  const bucket = ensureSpeciesBucket(speciesLabel);

  vegetationSeriesOrder.forEach(({ key }) => {
    const countValue = getVegetationCount(metadata, key);
    bucket[key] += countValue;
  });
};

/**
 * Shared label formatter used for visible bar segments
 */
const sharedLabelFormatter = (params: any): string => {
  const rawValue =
    params?.value != null && typeof params.value === 'object' && 'value' in params.value
      ? (params.value as any).value
      : params?.value;
  const numericValue = Number(rawValue);
  if (!Number.isFinite(numericValue) || numericValue === 0) return '';
  return String(numericValue);
};

/**
 * Creates a tooltip formatter for vegetation count bars.
 * @param key The vegetation count key.
 * @param totalsBySpecies The totals by species.
 * @returns A tooltip formatter function.
 */
const createVegetationTooltipFormatter =
  (key: VegetationCountKey, totalsBySpecies: Record<string, VegetationTotals>) => (params: any) => {
    const species = params?.name ? String(params.name) : '';
    const counts = species ? totalsBySpecies[species] : undefined;
    const valueForTooltip = counts ? counts[key] : params?.value;
    const safeValue = valueForTooltip != null ? valueForTooltip : 0;
    return `${params.marker || ''} ${params.seriesName}: ${safeValue}`.trim();
  };

/**
 * Builds a vegetation series option for the chart.
 * @param cfg The vegetation series configuration.
 * @param xAxisData The X-axis data for the chart.
 * @param totalsBySpecies The totals by species.
 * @param dataViewSpeciesOrder The species order for the data view.
 * @param legendLabels The legend labels for the chart.
 * @returns The vegetation series option.
 */
const buildVegetationSeriesOption = (
  cfg: VegetationSeriesConfig,
  xAxisData: string[],
  totalsBySpecies: Record<string, VegetationTotals>,
  dataViewSpeciesOrder: string[],
  legendLabels: string[]
): SeriesOption & VegetationSeriesDataViewMeta => {
  const { key, name, color: seriesColor } = cfg;

  legendLabels.push(name);

  const seriesValues = xAxisData.map((speciesLabel: string) => {
    const counts = totalsBySpecies[speciesLabel];
    if (!counts) return 0;
    return counts[key];
  });

  const tooltipFormatter = key === 'shrub_count' ? createVegetationTooltipFormatter(key, totalsBySpecies) : undefined;

  let labelConfig: any;
  if (key === 'tree_count') {
    labelConfig = {
      show: true,
      color: '#ffffff',
      fontSize: 9,
      position: 'right' as const,
      distance: 6,
      textBorderColor: seriesColor,
      textBorderWidth: 2,
      textBorderType: 'solid' as const,
      formatter: sharedLabelFormatter,
    };
  } else {
    labelConfig = {
      show: true,
      color: '#ffffff',
      fontSize: 9,
      position: 'insideRight' as const,
      textBorderColor: seriesColor,
      textBorderWidth: 2,
      textBorderType: 'solid' as const,
      formatter: sharedLabelFormatter,
    };
  }

  const itemStyle = undefined;

  const seriesOption = {
    name,
    type: 'bar',
    stack: 'total',
    emphasis: { focus: 'series' },
    label: labelConfig,
    data: seriesValues,
    ...(itemStyle ? { itemStyle } : {}),
    ...(tooltipFormatter ? { tooltip: { formatter: tooltipFormatter } } : {}),
  } as SeriesOption & VegetationSeriesDataViewMeta;

  if (key === 'tree_count') {
    seriesOption.dataViewTotals = totalsBySpecies;
    seriesOption.dataViewSpeciesOrder = dataViewSpeciesOrder;
  }

  return seriesOption;
};

/**
 * Formats the tooltip for terrestrial vegetation charts.
 * @param params The tooltip parameters.
 * @returns The formatted tooltip string.
 */
export const terrestrialVegetationTooltipFormatter = (params: any): string => {
  if (!Array.isArray(params)) return '';
  const category = params[0]?.axisValue ?? '';
  const lines = [];
  let total = 0;
  params.forEach((p: any) => {
    if (!p) return;
    const marker = p.marker || '';
    if (p.seriesType === 'scatter' && Array.isArray(p.value)) {
      const percentValue = Number(p.value[1]);
      const formattedPercent = Number.isFinite(percentValue) ? percentValue.toFixed(1) : '-';
      lines.push(`${marker} ${p.seriesName}: ${formattedPercent}%`);
    } else {
      const numeric = Number(p.value);
      total += Number.isFinite(numeric) ? numeric : 0;
      const formattedCount = Number.isFinite(numeric) ? String(numeric) : '-';
      lines.push(`${marker} ${p.seriesName}: ${formattedCount}`);
    }
  });

  // prepend total and category
  lines.unshift(`Total: ${total}`);
  lines.unshift(...(category ? [`${category}`] : []));

  return lines.join('<br/>');
};

/**
 * Creates a tooltip formatter for the terrestrial vegetation scatter series.
 */
export const createVegetationScatterTooltipFormatter = (
  totalSpeciesBySite: Map<string, Set<string>>,
  nativeSpeciesBySite: Map<string, Set<string>>
) => {
  return (params: any) => {
    if (!params) return '';
    const site = params.name ?? '';
    const percent = typeof params.value === 'number' && Number.isFinite(params.value) ? params.value : 0;
    const totalSpecies = totalSpeciesBySite.get(site)?.size ?? 0;
    const nativeSpecies = nativeSpeciesBySite.get(site)?.size ?? 0;
    return [
      `${params.marker || ''} ${params.seriesName}`,
      `Site: ${site}`,
      `Native species: ${nativeSpecies} of ${totalSpecies}`,
      `Percentage: ${percent.toFixed(1)}%`,
    ].join('<br/>');
  };
};

/**
 * Maps terrestrial vegetation data to a format suitable for the chart.
 * @param _keyedBySourceId The data keyed by source ID.
 * @param idToLabelMap A map of entity IDs to their labels.
 * @param xAxisData The X-axis data for the chart.
 * @param legendLabels The legend labels for the chart.
 * @param dataSource The data source for the chart.
 * @param seriesData The series data for the chart.
 * @returns The mapped series data for the chart.
 */
export const mapTerrestrialVegetationData: AggregateCartesianChartDataConfig['mapDataToSeries'] = (
  _keyedBySourceId,
  idToLabelMap,
  xAxisData,
  legendLabels,
  dataSource,
  seriesData,
  selectedSources
) => {
  const records = Array.isArray(seriesData?.data) ? seriesData.data : [];
  if (!records.length || !dataSource) return [];

  const allowedIdentifiers = buildAllowedIdentifierSet(selectedSources);
  const sourceIdToIdentifier = buildSourceIdToIdentifierMap(seriesData);
  const sourceLookup = buildSourceIdentifierLookup(seriesData);
  const terrestrialSeriesOrder = getTerrestrialVegetationSeriesOrder();
  const siteOrder: string[] = [];
  const perSite = new Map<string, TerrestrialVegetationCounts>();
  const nativeEntityIds = collectNativeEntityIds(seriesData);
  const totalSpeciesBySite = new Map<string, Set<string>>();
  const nativeSpeciesBySite = new Map<string, Set<string>>();

  for (const record of records) {
    const normalizedIdentifier = resolveNormalizedSourceIdentifier(record, sourceIdToIdentifier);
    if (allowedIdentifiers && (!normalizedIdentifier || !allowedIdentifiers.has(normalizedIdentifier))) {
      continue;
    }

    processTerrestrialRecord(
      record,
      sourceLookup,
      idToLabelMap,
      perSite,
      siteOrder,
      nativeEntityIds,
      totalSpeciesBySite,
      nativeSpeciesBySite,
      terrestrialSeriesOrder
    );
  }

  if (!siteOrder.length) return [];

  xAxisData.length = 0;
  xAxisData.push(...siteOrder);
  legendLabels.length = 0;

  const barSeries = terrestrialSeriesOrder.map(({ key, name }) => {
    if (!legendLabels.includes(name)) legendLabels.push(name);

    return {
      name,
      type: 'bar',
      yAxisIndex: 0,
      emphasis: { focus: 'series' },
      data: siteOrder.map((site) => perSite.get(site)?.[key] ?? 0),
    } as SeriesOption;
  });

  const calculateNativePercentage = (site: string): number => {
    const totalSpecies = totalSpeciesBySite.get(site)?.size ?? 0;
    if (!totalSpecies) return 0;
    const nativeSpecies = nativeSpeciesBySite.get(site)?.size ?? 0;
    const ratio = (nativeSpecies / totalSpecies) * 100;
    const rounded = Math.round(ratio * 10) / 10;
    return Number.isFinite(rounded) ? rounded : 0;
  };

  const scatterSeriesName = 'Native Species Count (%)';
  if (!legendLabels.includes(scatterSeriesName)) legendLabels.push(scatterSeriesName);

  /**
   * Scatter series showing native species percentage per site.
   */
  const vegetationCompScatterSeries: SeriesOption = {
    name: scatterSeriesName,
    type: 'scatter',
    yAxisIndex: 1,
    symbol: 'circle',
    symbolSize: 12,
    emphasis: { focus: 'series' },
    data: siteOrder.map((site) => ({ name: site, value: calculateNativePercentage(site) })),
    tooltip: {
      formatter: createVegetationScatterTooltipFormatter(totalSpeciesBySite, nativeSpeciesBySite),
    },
  };

  return [...barSeries, vegetationCompScatterSeries];
};

/**
 * Maps wetlands vegetation species richness data to a format suitable for the chart.
 * @param _keyedBySourceId The data keyed by source ID.
 * @param _idToLabelMap A map of entity IDs to their labels.
 * @param xAxisData The X-axis data for the chart.
 * @param legendLabels The legend labels for the chart.
 * @param dataSource The data source for the chart.
 * @param seriesData The series data for the chart.
 * @returns The mapped series data for the chart.
 */
export const wetlandsVegetationSpeciesMapper: AggregateCartesianChartDataConfig['mapDataToSeries'] = (
  _keyedBySourceId,
  idToLabelMap,
  xAxisData,
  legendLabels,
  dataSource,
  seriesData,
  selectedSources
) => {
  const observations: {
    [treatment: string]: { [species: string]: { shrub_count: number; sapling_count: number; tree_count: number } };
  } = {};

  const entityArray: any[] = Array.isArray(seriesData?.entities) ? seriesData!.entities : [];
  const entityLookup = entityArray.reduce((acc: any, entity: any) => {
    acc[entity.id] = entity.name;
    return acc;
  }, {});

  const sourceInfoMap = new Map<string, { treatment: string }>();
  if (seriesData?.environmentalDataSources) {
    seriesData.environmentalDataSources.forEach((source: any) => {
      if (source.id) {
        sourceInfoMap.set(String(source.id), {
          treatment: source.source_metadata?.Treatment || 'Unknown',
        });
      }
    });
  }

  const nativeEntityIds = collectNativeEntityIds(seriesData);

  const allowedIdentifiers = buildAllowedIdentifierSet(selectedSources);
  const sourceIdToIdentifier = buildSourceIdToIdentifierMap(seriesData);

  const dataPoints: any[] = Array.isArray(seriesData?.data) ? seriesData.data : [];

  const nativeSpeciesByTreatment: { [treatment: string]: Set<string> } = {};

  dataPoints.forEach((item: any) => {
    const normalizedIdentifier = resolveNormalizedSourceIdentifier(item, sourceIdToIdentifier);
    if (allowedIdentifiers && (!normalizedIdentifier || !allowedIdentifiers.has(normalizedIdentifier))) {
      return;
    }

    const sourceId = String(item.environmental_data_source_id);
    const sourceInfo = sourceInfoMap.get(sourceId);
    const treatment = sourceInfo?.treatment || 'Unknown';

    const speciesId = item.observation_entity_id;
    const species = entityLookup[speciesId] || 'Unknown Species';
    const metadata = parseVegetationMetadata(item.collection_metadata);

    if (nativeEntityIds.has(String(speciesId))) {
      if (!nativeSpeciesByTreatment[treatment]) {
        nativeSpeciesByTreatment[treatment] = new Set();
      }
      nativeSpeciesByTreatment[treatment].add(species);
    }

    if (!observations[treatment]) {
      observations[treatment] = {};
    }
    if (!observations[treatment][species]) {
      observations[treatment][species] = { shrub_count: 0, sapling_count: 0, tree_count: 0 };
    }

    observations[treatment][species].shrub_count += getVegetationCount(metadata, 'shrub_count');
    observations[treatment][species].sapling_count += getVegetationCount(metadata, 'sapling_count');
    observations[treatment][species].tree_count += getVegetationCount(metadata, 'tree_count');
  });

  const treatments = Object.keys(observations).sort();
  xAxisData.length = 0;
  xAxisData.push(...treatments);

  const allSpecies = [
    ...new Set(
      Object.values(observations).reduce((acc: string[], obs: any) => {
        return acc.concat(Object.keys(obs));
      }, [])
    ),
  ].sort();

  const series: SeriesOption[] = [];
  legendLabels.length = 0;
  legendLabels.push(...allSpecies);

  const vegetationSeriesOrder = getVegetationSeriesOrder();

  allSpecies.forEach((species, i) => {
    const baseColor = color[i % color.length];
    const lighterColor = lightenColor(baseColor, 20); // 20% lighter
    const lightestColor = lightenColor(baseColor, 40); // 40% lighter

    const speciesColors = {
      tree_count: baseColor,
      sapling_count: lighterColor,
      shrub_count: lightestColor,
    };

    vegetationSeriesOrder.forEach((vegType) => {
      series.push({
        name: `${species} (${vegType.name})`,
        type: 'bar',
        stack: species,
        legendHoverLink: false,
        data: treatments.map((treatment) => observations[treatment]?.[species]?.[vegType.key] || 0),
        itemStyle: {
          color: speciesColors[vegType.key],
        },
        tooltip: {
          valueFormatter: (value) => `${value} (${vegType.name})`,
        },
      });
    });
  });

  const nativePercentageSeries: SeriesOption = {
    name: 'Native Percentage',
    type: 'scatter',
    symbol: 'circle',
    symbolSize: 12,
    emphasis: { focus: 'series' },
    yAxisIndex: 1, // Assuming a second y-axis for percentage
    data: treatments.map((treatment) => {
      const totalSpeciesInTreatment = Object.keys(observations[treatment] || {}).length;
      const nativeSpeciesCount = nativeSpeciesByTreatment[treatment]?.size || 0;
      if (totalSpeciesInTreatment === 0) {
        return 0;
      }
      return (nativeSpeciesCount / totalSpeciesInTreatment) * 100;
    }),
    tooltip: {
      valueFormatter: (value) => `${(value as number).toFixed(2)}%`,
    },
  };

  series.push(nativePercentageSeries);
  legendLabels.push('Native Percentage');

  return series;
};

/**
 * Rebuilds the data view for a specific series.
 * @param series The series to rebuild.
 * @param speciesOrder The order of species.
 * @param totalsBySpecies The total counts by species.
 * @returns The rebuilt series with updated data view.
 */
const reformatForDataView = (
  series: any,
  speciesOrder: string[],
  totalsBySpecies: Record<string, VegetationTotals>
) => {
  if (!series || typeof series !== 'object') return series;
  const allowed = ['Shrub count', 'Sapling count', 'Tree count'];
  if (!allowed.includes(series.name)) return series;

  const data = speciesOrder.map((species) => {
    const counts = totalsBySpecies[species] || initialiseCounts();
    switch (series.name) {
      case 'Shrub count':
        return counts.shrub_count;
      case 'Sapling count':
        return counts.sapling_count;
      default:
        return counts.tree_count;
    }
  });

  return {
    ...series,
    data,
  };
};

/**
 * Converts a wetlands vegetation species richness chart data into a well-formatted data view table.
 * Uses the y-axis name as the first-column header when available.
 *
 * @param option The ECharts option to convert.
 * @returns A string or HTMLElement representing the content.
 */
export const formatWetlandVegetationSpeciesDV = (option: EChartsOption): string | HTMLElement => {
  const seriesArray = Array.isArray(option.series) ? option.series : [];

  console.log('Original series for data view:', seriesArray);

  const enrichedSeries = seriesArray.find(
    (series: any) => series && typeof series === 'object' && series.dataViewTotals && series.dataViewSpeciesOrder
  ) as (SeriesOption & VegetationSeriesDataViewMeta) | undefined;

  console.log('Enriched series for data view:', enrichedSeries);

  if (!enrichedSeries || !enrichedSeries.dataViewTotals || !enrichedSeries.dataViewSpeciesOrder) {
    return dataViewFormatter(option);
  }

  const speciesOrder = enrichedSeries.dataViewSpeciesOrder;
  const totalsBySpecies = enrichedSeries.dataViewTotals;
  const rebuiltSeries = seriesArray.map((series: any) => reformatForDataView(series, speciesOrder, totalsBySpecies));
  const rebuiltOption: EChartsOption = {
    ...option,
    series: rebuiltSeries,
    xAxis: Array.isArray(option.xAxis)
      ? option.xAxis.map((axis: any, index: number) => {
          if (index !== 0 || !axis || typeof axis !== 'object') return axis;
          return {
            ...axis,
            type: 'category',
            name:
              (Array.isArray(option.yAxis) && option.yAxis[0] && (option.yAxis[0] as any).name) ||
              axis.name ||
              'Category',
            data: speciesOrder,
          } as any;
        })
      : {
          ...(typeof option.xAxis === 'object' ? option.xAxis : {}),
          type: 'category',
          name: (Array.isArray(option.yAxis) && option.yAxis[0] && (option.yAxis[0] as any).name) || 'Category',
          data: speciesOrder,
        },
    yAxis: Array.isArray(option.yAxis)
      ? option.yAxis.map((axis: any, index: number) => {
          if (index !== 0 || !axis || typeof axis !== 'object') return axis;
          return {
            ...axis,
            data: speciesOrder,
          } as any;
        })
      : option.yAxis,
  };

  return dataViewFormatter(rebuiltOption);
};

/**
 * Lightens the given hex color by the specified percentage.
 * @param hex The base color in hex format.
 * @param percent The percentage to lighten (e.g., 10 for 10%).
 * @returns The lightened color in hex format.
 */
const lightenColor = (hex: string, percent: number): string => {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = ((num >> 8) & 0x00ff) + amt;
  const B = (num & 0x0000ff) + amt;

  const toHex = (c: number) => `00${c.toString(16)}`.slice(-2);

  return `#${toHex(R < 255 ? (R < 1 ? 0 : R) : 255)}${toHex(G < 255 ? (G < 1 ? 0 : G) : 255)}${toHex(
    B < 255 ? (B < 1 ? 0 : B) : 255
  )}`;
};
